import { Chip, TextField } from '@material-ui/core'
import {
    Autocomplete,
    AutocompleteGetTagProps,
    AutocompleteRenderInputParams,
    AutocompleteRenderOptionState
} from '@material-ui/lab'
import React, {
    ChangeEvent,
    MutableRefObject,
    ReactElement,
    ReactNode,
    RefObject,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState
} from 'react'
import useIsMountedRef from 'use-is-mounted-ref'
import { useFormContext } from '../../../../Vendor/react-hook-form/dist/index.ie11'
import { ComboBox as ComboBoxLanguage } from '../../../../../i18n'
import { EventNames } from '../../../../NonReact/constants/EventNames'
import CLanguageHandler from '../../../../NonReact/Languages/LanguageHandler'
import { IsVarEmpty } from '../../../../NonReact/utils/GlobalFunctions'
import {
    $addBodyEventListener,
    $addEventListener,
    $removeBodyEventListener,
    $removeEventListener
} from '../../../../NonReact/utils/WindowFunctions'
import { LanguageContext } from '../../Contexts'
import { CommonFormContext, FormContextType } from '../../Contexts/CommonFormContext'
import { ReactState, Rules } from '../../Types/Types'
import Checkbox from '../Checkbox'
import Label from '../Label/Label'
import Loader from '../Loader'
import { ComboBoxItem } from '../Selects/Types'
import { EMPTY_STRING } from '../../../../NonReact/constants/Common'

type MultiProps<T> = {
    name: string
    defaultOption: ComboBoxItem<T>
    options: ComboBoxItem<T>[]
    label: string
    multiple: true
    rules?: Rules
    onChange?: (option: Nullable<ComboBoxItem<T>[]>) => void
    loading?: boolean
    disabled?: boolean
    noChange?: boolean
    variant?: 'outlined' | 'underlined'
}

type Props<T> = {
    name: string
    defaultOption: ComboBoxItem<T>
    options: ComboBoxItem<T>[]
    label: string
    multiple?: false
    rules?: Rules
    onChange?: (option: Nullable<ComboBoxItem<T>>) => void
    loading?: boolean
    disabled?: boolean
    noChange?: boolean
    variant?: 'outlined' | 'underlined'
}

const MUI_END_ADORNMENT_CLASS: string = '.MuiAutocomplete-endAdornment'

export const ComboBox = <T extends unknown>({
    name,
    defaultOption,
    options,
    label,
    rules,
    onChange,
    multiple = false,
    loading,
    disabled,
    noChange,
    variant
}: Props<T> | MultiProps<T>): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle('ComboBox')

    const { register, watch, setValue, trigger } = useFormContext()
    const commonForm: Nullable<FormContextType> = useContext(CommonFormContext)

    const defaultedOption: ComboBoxItem<T> | ComboBoxItem<T>[] = multiple ? [defaultOption] : defaultOption

    const [defaultValue]: ReactState<ComboBoxItem<T> | ComboBoxItem<T>[]> = useState<
        ComboBoxItem<T> | ComboBoxItem<T>[]
    >(defaultedOption)
    const [acValue, setAcValue]: ReactState<ComboBoxItem<T> | ComboBoxItem<T>[]> = useState<
        ComboBoxItem<T> | ComboBoxItem<T>[]
    >(defaultedOption)
    const [isOpen, setIsOpen]: ReactState<boolean> = useState<boolean>(false)

    const disabledRef: MutableRefObject<boolean> = useRef<boolean>(!!disabled)
    const noChangeRef: MutableRefObject<boolean> = useRef<boolean>(!!noChange)

    const normalisedOptions: ComboBoxItem<T>[] =
        !defaultOption && options?.length === 0
            ? [Array.isArray(defaultValue) ? defaultValue[0] : defaultValue]
            : [defaultOption, ...options]
    const hookFieldValue: T | T[] = watch(name)

    const isMounted: MutableRefObject<boolean> = useIsMountedRef()

    const updateValueFromHookField = (): void => {
        if (!IsVarEmpty(hookFieldValue)) {
            const filteredOptions: ComboBoxItem<T>[] = normalisedOptions.filter((option: ComboBoxItem<T>): boolean =>
                multiple ? (hookFieldValue as T[]).includes(option.value) : option.value === hookFieldValue
            )
            const finalFilterOptions: ComboBoxItem<T>[] = filteredOptions.length > 0 ? filteredOptions : [defaultOption]

            if (multiple) {
                setTimeout((): void => {
                    if (isMounted.current) {
                        setAcValue(finalFilterOptions)
                    }
                })
            } else {
                setTimeout((): void => {
                    if (isMounted.current) {
                        setAcValue(finalFilterOptions[0])
                    }
                })
            }
        }

        setTimeout((): void => {
            trigger(name)
        })
    }

    useEffect((): void => {
        register(name, rules)
    }, [name, rules])

    useEffect(updateValueFromHookField, [JSON.stringify(hookFieldValue), JSON.stringify(options)])

    useEffect((): void => {
        if (defaultOption) {
            updateValueFromHookField()

            if (
                Array.isArray(acValue) &&
                acValue.length === 1 &&
                defaultOption.value === acValue[0].value &&
                defaultOption.label !== acValue[0].label
            ) {
                setAcValue([defaultedOption as ComboBoxItem<T>])
            } else if (
                !Array.isArray(acValue) &&
                acValue.value === defaultOption.value &&
                acValue.label !== defaultOption.label
            ) {
                setAcValue(defaultedOption)
            }
        }
    }, [JSON.stringify(defaultOption)])

    const isDefault: boolean = multiple
        ? (hookFieldValue as T[])[0] === defaultOption?.value
        : hookFieldValue === defaultOption?.value

    const ref: RefObject<HTMLElement> = useRef(null)
    const inputRef: RefObject<HTMLInputElement> = useRef(null)

    const handleClickAway = useCallback((event?: MouseEvent): void => {
        if (event) {
            const target: HTMLElement = event.target as HTMLElement
            const popperContainer: Nullable<Element> = document.querySelector('.MuiAutocomplete-popper')

            if (target && !ref.current?.contains(target) && !popperContainer?.contains(target)) {
                setIsOpen(false)
            }
        }

        return undefined
    }, [])

    const handleEndAdornmentClick = useCallback((event: Event): void => {
        event?.preventDefault()

        if (!disabledRef.current && !noChangeRef.current) {
            setIsOpen((open: boolean): boolean => !open)
        }
    }, [])

    const handleEscapeCatch = useCallback((event?: KeyboardEvent): void => {
        if (event?.key === 'Escape') {
            event?.preventDefault()
            setIsOpen(false)
        }
    }, [])

    useEffect(() => {
        disabledRef.current = !!disabled
    }, [disabled])

    useEffect(() => {
        noChangeRef.current = !!noChange
    }, [noChange])

    useEffect((): void => {
        if (isOpen) {
            $addEventListener(EventNames.CLICK, handleClickAway)
            $addBodyEventListener(EventNames.KEYDOWN, handleEscapeCatch)
        } else {
            $removeEventListener(EventNames.CLICK, handleClickAway)
            $removeBodyEventListener(EventNames.KEYDOWN, handleEscapeCatch)
        }
    }, [isOpen])

    useEffect((): (() => void) => {
        if (ref.current && !disabled && !noChange) {
            const endAdornment: Nullable<Element> = ref.current.querySelector(MUI_END_ADORNMENT_CLASS)

            if (endAdornment) {
                endAdornment.addEventListener(EventNames.CLICK, handleEndAdornmentClick)
            }
        }

        return (): void => {
            if (ref.current) {
                const endAdornment: Nullable<Element> = ref.current.querySelector(MUI_END_ADORNMENT_CLASS)

                if (endAdornment) {
                    endAdornment.removeEventListener(EventNames.CLICK, handleEndAdornmentClick)
                }
            }
        }
    }, [disabled, noChange, handleEndAdornmentClick])

    return (
        <Autocomplete
            open={isOpen}
            ref={ref}
            className={`combo-box-container ${noChange ? 'no-change' : EMPTY_STRING} cb-variant-${
                variant ?? 'underlined'
            }`}
            classes={{
                popper: `combo-box-popper cb-popper-variant-${variant ?? 'underlined'}`
            }}
            loading={loading}
            disabled={disabled || noChange}
            loadingText={language.get(ComboBoxLanguage.LoadingLabel)}
            value={acValue}
            options={loading ? [] : normalisedOptions}
            defaultValue={defaultValue}
            disableClearable={isDefault}
            getOptionLabel={(option: ComboBoxItem<T>): string => option.label}
            getOptionSelected={(option: ComboBoxItem<T>, selected: ComboBoxItem<T>): boolean => {
                if (!option) return false

                return (
                    (option.value === defaultOption?.value &&
                        !options.some((opt: ComboBoxItem<T>): boolean => opt.value === selected?.value)) ||
                    option.value === selected.value
                )
            }}
            multiple={multiple}
            disableCloseOnSelect={multiple}
            renderTags={(op: ComboBoxItem<T>[], getTagProps: AutocompleteGetTagProps): ReactNode =>
                op.map((option, index) => (
                    <Chip key={option.label} label={option.label} title={option.label} {...getTagProps({ index })} />
                ))
            }
            onFocus={(): void => setIsOpen(true)}
            onBlur={(): void => setIsOpen(false)}
            // eslint-disable-next-line
            onChange={(ignored: ChangeEvent<{}>, option: Nullable<ComboBoxItem<T> | ComboBoxItem<T>[]>): void => {
                if (!noChange) {
                    if (commonForm?.handleFormChange) {
                        commonForm?.handleFormChange(name)
                    }
                    let finalOption: Nullable<ComboBoxItem<T> | ComboBoxItem<T>[]> = option

                    if (Array.isArray(finalOption)) {
                        if (
                            finalOption.length > 1 &&
                            finalOption.some((opt: ComboBoxItem<T>): boolean => opt.value === defaultOption?.value)
                        ) {
                            if ((hookFieldValue as T[]).includes(defaultOption?.value)) {
                                finalOption.splice(
                                    finalOption.findIndex(
                                        (v: ComboBoxItem<T>): boolean => v.value === defaultOption?.value
                                    ),
                                    1
                                )
                            } else {
                                finalOption = [defaultOption]
                            }
                        }

                        const values: T[] = finalOption.map((opt: ComboBoxItem<T>): T => opt.value)

                        setValue(name, values && values.length > 0 ? values : [defaultOption?.value])
                    } else if (finalOption?.value !== hookFieldValue) {
                        setValue(name, finalOption?.value ?? defaultOption?.value)
                    }

                    if (onChange) {
                        onChange(
                            (finalOption ?? (defaultedOption as unknown)) as Nullable<
                                ComboBoxItem<T> & ComboBoxItem<T>[]
                            >
                        )
                    }
                } else {
                    setAcValue(
                        (prevValue: ComboBoxItem<T> | ComboBoxItem<T>[]): ComboBoxItem<T> | ComboBoxItem<T>[] =>
                            prevValue
                    )
                }

                if (!multiple) {
                    setIsOpen(false)
                }

                setTimeout((): void => inputRef.current?.focus())
            }}
            renderOption={(op: ComboBoxItem<T>, { selected }: AutocompleteRenderOptionState): ReactNode => (
                <div className='combo-option'>
                    {multiple && <Checkbox isChecked={selected} />}
                    {op.label}
                </div>
            )}
            renderInput={(params: AutocompleteRenderInputParams): ReactNode => {
                if (loading) {
                    params.InputProps.endAdornment = (
                        <div className='combo-box-load-container'>
                            {params.InputProps.endAdornment}
                            <Loader size={16} />
                        </div>
                    )
                }

                return (
                    <div
                        className={`text-input combo-box ${multiple ? 'multi-cb' : ''} ${disabled ? 'disabled' : ''}`}
                        onClick={(): void => inputRef.current?.focus()}
                    >
                        <Label customClass='label-left'>{label}</Label>
                        <div className='input'>
                            <TextField {...params} inputRef={inputRef} />
                        </div>
                    </div>
                )
            }}
        />
    )
}
